import type { Processed, ProcessImage } from './import';
import type { WorkerJob, WorkerResult } from './pipeline.worker';

/**
 * At most `size` workers, each handling one file at a time. A worker that crashes or stays silent past
 * `timeoutMs` fails its job and is terminated, and a fresh one takes its slot, so a bad file never wedges the pool.
 */
export function createWorkerPool(opts: { size: number; timeoutMs: number; spawn: () => Worker }): ProcessImage {
  const idle: Worker[] = [];
  const waiting: ((w: Worker) => void)[] = [];
  let alive = 0;

  const acquire = (): Promise<Worker> => {
    const w = idle.pop();
    if (w) return Promise.resolve(w);
    if (alive < opts.size) {
      alive++;
      return Promise.resolve(opts.spawn());
    }
    return new Promise((resolve) => waiting.push(resolve));
  };
  const release = (w: Worker) => {
    const next = waiting.shift();
    if (next) next(w);
    else idle.push(w);
  };
  const discard = (w: Worker) => {
    w.terminate();
    alive--;
    const next = waiting.shift();
    if (next) {
      alive++;
      next(opts.spawn());
    }
  };

  return async (file, job) => {
    const w = await acquire();
    let broken = false;
    try {
      const out = await new Promise<Processed>((resolve, reject) => {
        const timer = setTimeout(() => {
          broken = true;
          reject(new Error('timeout'));
        }, opts.timeoutMs);
        w.onmessage = (e: MessageEvent<WorkerResult>) => {
          clearTimeout(timer);
          if (e.data.ok) resolve(e.data);
          else reject(new Error(e.data.error));
        };
        w.onerror = (e) => {
          clearTimeout(timer);
          broken = true;
          reject(new Error(e.message || 'worker error'));
        };
        w.postMessage({ file, posterOnly: job?.posterOnly ?? false } satisfies WorkerJob);
      });
      return out;
    } finally {
      if (broken) discard(w);
      else release(w);
    }
  };
}
